import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserSidebarLayout } from '../components/UserSidebarLayout';
import { COLORS } from '../../../core/constants/colors';
import { Company, Query } from '../types';
import { api } from '../../../core/utils/apiClient';
import { RecommendationsSection } from '../../recommendations/components/RecommendationsSection';
import { linkedInEnrichmentService } from '../../linkedin_enrichment/services/linkedin-enrichment.service';
import { LinkedInEnrichmentData } from '../../linkedin_enrichment/types';
import { webEnrichmentService } from '../../web_enrichment/services/web-enrichment.service';
import { WebEnrichmentData } from '../../web_enrichment/types';
import { useSubscription } from '../../../app/providers/SubscriptionProvider';
import { CompanyOverviewTab } from '../../companies/components/CompanyOverviewTab';
import { useAuth } from '../../../app/providers/AuthProvider';
import { WebEnrichmentDisplay } from '../../companies/components/WebEnrichmentDisplay';
import { WebOverridePendingReview } from '../../companies/components/WebOverridePendingReview';

export const CompanyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const { subscriptionStatus } = useSubscription();
  const [company, setCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about');
  const [queryHistory, setQueryHistory] = useState<Query[]>([]);
  const [queryText, setQueryText] = useState('');
  const [isSubmittingQuery] = useState(false);
  const [linkedInData, setLinkedInData] = useState<LinkedInEnrichmentData | null>(null);
  const [isLoadingLinkedIn, setIsLoadingLinkedIn] = useState(false);
  const [webEnrichmentData, setWebEnrichmentData] = useState<WebEnrichmentData | null>(null);
  const [isLoadingWebEnrichment, setIsLoadingWebEnrichment] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState<string>('viewer');
  const isSubscriptionActive = subscriptionStatus?.is_active ?? false;
  const isCompanyAdmin = currentUserRole === 'admin' || currentUserRole === 'owner';

  useEffect(() => {
    loadCompanyData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadCompanyData = async () => {
    try {
      setIsLoading(true);

      // Load company details
      const companyResponse = await api.get(`/api/v1/companies/${id}`);

      if (companyResponse.ok) {
        const companyData = await companyResponse.json();
        setCompany(companyData);
      }

      // Resolve the current user's role in this company.
      // Primary: /my-companies returns the correct user_role for the authenticated user.
      // Fallback: member list matched by auth identity (email or id).
      try {
        const myCompaniesRes = await api.get('/api/v1/companies/my-companies');
        if (myCompaniesRes.ok) {
          const list = await myCompaniesRes.json();
          const companies = Array.isArray(list) ? list : list.companies || [];
          const match = companies.find((c: any) => c.id === parseInt(id!));
          if (match?.user_role) {
            setCurrentUserRole(match.user_role);
          } else {
            // Fallback: match member entry by the authenticated user's identity
            const membersRes = await api.get(`/api/v1/companies/${id}/members`);
            if (membersRes.ok) {
              const members = await membersRes.json();
              const currentUserEntry = members.find(
                (m: any) =>
                  m.user_id === authState.user?.email ||
                  m.user_id === authState.user?.id
              );
              if (currentUserEntry?.role) setCurrentUserRole(currentUserEntry.role);
            }
          }
        }
      } catch {
        // role stays 'viewer' — backend will reject any unauthorised writes
      }

      // Load query history for this company
      const historyResponse = await api.get('/api/v1/llm/query/history?limit=10');

      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        const companyQueries = (historyData || [])
          .filter((q: Query) => q.company_id === parseInt(id!));
        setQueryHistory(companyQueries);
      }

      // Load LinkedIn data for this company
      setIsLoadingLinkedIn(true);
      try {
        const linkedInResponse = await linkedInEnrichmentService.getData(parseInt(id!));
        setLinkedInData(linkedInResponse);
      } catch (error) {
        console.error('Error loading LinkedIn data:', error);
        // LinkedIn data is optional, so we don't fail the entire page load
      } finally {
        setIsLoadingLinkedIn(false);
      }

      // Load Web enrichment data for this company
      setIsLoadingWebEnrichment(true);
      try {
        const webEnrichmentResponse = await webEnrichmentService.getData(parseInt(id!));
        setWebEnrichmentData(webEnrichmentResponse);
      } catch (error) {
        console.error('Error loading web enrichment data:', error);
        // Web enrichment data is optional, so we don't fail the entire page load
      } finally {
        setIsLoadingWebEnrichment(false);
      }
    } catch (error) {
      console.error('Error loading company data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getCompanyInitial = (name: string) => {
    return name?.charAt(0).toUpperCase() || 'C';
  };

  const getInitialColor = (name: string) => {
    const colors = [
      COLORS.user.primary,
      COLORS.extended.orange,
      COLORS.extended.azure,
      COLORS.primary.deepSoftLime,
    ];
    const index = name?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };

  const formatWebsiteUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  const handleSubmitQuery = (e: React.FormEvent) => {
    e.preventDefault();
    const text = queryText.trim();
    if (!text || !company?.id) return;

    // Hand the question to the chat page scoped to this company. The chat page
    // auto-submits the pendingQuery; the backend scopes the new conversation to
    // companyId so the agent answers about this company specifically.
    setQueryText('');
    navigate('/dashboard/chat', {
      state: {
        pendingQuery: text,
        isNewQuery: true,
        newChat: true,
        companyId: company.id,
      },
    });
  };

  if (isLoading) {
    return (
      <UserSidebarLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
              style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
            />
            <p style={{ color: COLORS.primary.persianIndigo }}>Loading company...</p>
          </div>
        </div>
      </UserSidebarLayout>
    );
  }

  if (!company) {
    return (
      <UserSidebarLayout>
        <div className="text-center py-12">
          <p className="text-gray-600">Company not found</p>
        </div>
      </UserSidebarLayout>
    );
  }

  return (
    <UserSidebarLayout>
      <div className="min-h-screen bg-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>
        {/* Header Section */}
        <div className="bg-white border-b" style={{ borderColor: '#E5E7EB' }}>
          <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Breadcrumbs */}
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
            <button onClick={() => navigate('/dashboard/companies')} className="hover:underline">
              Companies
            </button>
            <span>›</span>
              <span className="text-gray-900 font-medium">{company.name}</span>
          </div>

          {/* Company Header */}
            <div className="flex items-start gap-4 mb-6">
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center text-white text-2xl font-bold flex-shrink-0"
                style={{ backgroundColor: getInitialColor(company.name) }}
              >
                {getCompanyInitial(company.name)}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">
                  {company.name}
                </h1>
                </div>
                <p className="text-sm text-gray-600">
                  {company.industry || 'Industry'} · {company.location || 'Location'} · {company.employees || 'N/A'} Employees
                </p>
              </div>
            </div>

            {/* Overview Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {company.location && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Headquarters</p>
                  <p className="text-sm text-gray-900">{company.location}</p>
                    </div>
                  )}
              {company.website && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Website</p>
                  <a
                    href={formatWebsiteUrl(company.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    {company.website}
                  </a>
                    </div>
                  )}
              {company.founding_year && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Founded</p>
                  <p className="text-sm text-gray-900">{company.founding_year}</p>
                </div>
              )}
              {company.employees && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Employees</p>
                  <p className="text-sm text-gray-900">{company.employees}</p>
                    </div>
                  )}
                </div>

            {/* Industry Tags + Qualification Badge */}
            {(company.industry || company.qualification_name) && (
              <div className="flex flex-wrap gap-2">
                {company.industry && company.industry.split(',').map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
                  >
                    {tag.trim()}
                  </span>
                ))}
                {company.qualification_name && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                    {company.qualification_name}
                  </span>
                )}
              </div>
            )}
        </div>
      </div>

      {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2">
              {/* Navigation Tabs */}
              <div className="border-b mb-6" style={{ borderColor: '#E5E7EB' }}>
                <div className="flex gap-6">
                  {[...(isCompanyAdmin ? ['overview'] : []), 'about', 'web-info', 'leadership', 'job-openings', 'similar', 'queries'].map((tab) => (
                  <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`pb-3 px-1 text-sm font-medium capitalize transition-colors flex items-center gap-2 ${
                        activeTab === tab
                          ? 'text-blue-600 border-b-2'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      style={activeTab === tab ? { borderBottomColor: COLORS.user.primary } : {}}
                    >
                      {tab === 'similar' ? (
                        <>
                          Similar Companies
                          {!isSubscriptionActive && (
                            <svg
                              className="w-4 h-4"
                              style={{ color: '#9CA3AF' }}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                              />
                            </svg>
                          )}
                        </>
                      ) : tab === 'queries' ? 'Query History' :
                       tab === 'job-openings' ? 'Job Openings' :
                       tab === 'web-info' ? 'Web Info' :
                       tab === 'overview' ? 'Overview' : tab}
                  </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="bg-white rounded-lg border p-6" style={{ borderColor: '#E5E7EB' }}>
                {activeTab === 'overview' && isCompanyAdmin && (
                  <CompanyOverviewTab company={company} />
                )}

                {activeTab === 'about' && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">About {company.name}</h3>
                    {company.description ? (
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap mb-6">
                        {company.description}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500 italic mb-6">No description available for this company.</p>
                    )}

                    {/* Recent Posts from LinkedIn */}
                    {linkedInData?.extracted_data && (() => {
                      const updates = linkedInData.extracted_data?.additional_info?.updates || 
                                     linkedInData.extracted_data?.updates || [];
                      const updatesList = Array.isArray(updates) ? updates : [];
                      
                      return updatesList.length > 0 && (
                        <div className="mt-6">
                          <h4 className="text-base font-semibold mb-4 text-gray-900">
                            Recent Posts
                          </h4>
                          <div className="space-y-4">
                            {updatesList.slice(0, 5).map((update: any, index: number) => (
                              <div key={index} className="p-4 rounded-lg border border-gray-200 bg-white">
                                {typeof update === 'object' ? (
                                  <>
                                    <div className="flex items-start justify-between mb-2">
                                      {update.title && (
                                        <p className="text-sm font-semibold text-gray-900">
                                          {update.title}
                                        </p>
                                      )}
                                      {update.time && (
                                        <span className="text-xs text-gray-500">{update.time}</span>
                                      )}
                                    </div>
                                    
                                    {update.text && (
                                      <p className="text-sm mb-3 leading-relaxed text-gray-700">
                                        {update.text}
                                      </p>
                                    )}

                                    {/* Images */}
                                    {update.images && Array.isArray(update.images) && update.images.length > 0 && (
                                      <div className="mb-3">
                                        <div className={`grid gap-2 ${update.images.length === 1 ? 'grid-cols-1' : update.images.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                                          {update.images.slice(0, 3).map((image: string, imgIndex: number) => (
                                            <img
                                              key={imgIndex}
                                              src={image}
                                              alt={`Post ${imgIndex + 1}`}
                                              className="w-full h-48 object-cover rounded-lg border border-gray-200"
                                              onError={(e) => {
                                                (e.target as HTMLImageElement).style.display = 'none';
                                              }}
                                            />
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Videos */}
                                    {update.videos && Array.isArray(update.videos) && update.videos.length > 0 && (
                                      <div className="mb-3">
                                        {update.videos.slice(0, 1).map((video: string, vidIndex: number) => (
                                          <video
                                            key={vidIndex}
                                            src={video}
                                            controls
                                            className="w-full rounded-lg border border-gray-200"
                                            style={{ maxHeight: '400px' }}
                                          >
                                            Your browser does not support the video tag.
                                          </video>
                                        ))}
                                      </div>
                                    )}

                                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                                      <div className="flex items-center gap-4 text-xs text-gray-500">
                                        {update.likes_count !== undefined && (
                                          <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                                            </svg>
                                            {update.likes_count}
                                          </span>
                                        )}
                                        {update.comments_count !== undefined && (
                                          <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                                            </svg>
                                            {update.comments_count}
                                          </span>
                                        )}
                                      </div>
                                      {update.post_url && (
                                        <a
                                          href={update.post_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-xs text-blue-600 hover:underline font-medium"
                                        >
                                          View on LinkedIn →
                                        </a>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <p className="text-sm text-gray-700">{String(update)}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {activeTab === 'leadership' && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Leadership</h3>
                    {(isLoadingLinkedIn || isLoadingWebEnrichment) ? (
                      <div className="flex items-center justify-center py-8">
                        <div
                          className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-transparent"
                          style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
                        />
                      </div>
                    ) : (() => {
                      // Get leadership from web enrichment first, then LinkedIn as fallback
                      const webLeadership = webEnrichmentData?.extracted_data?.leadership || 
                                           webEnrichmentData?.extracted_data?.leadership_team || 
                                           webEnrichmentData?.extracted_data?.leaders || [];
                      const linkedInLeadership = linkedInData?.extracted_data?.leadership || 
                                                 linkedInData?.extracted_data?.leaders || 
                                                 linkedInData?.extracted_data?.leadership_team || [];
                      
                      // Combine both sources, prioritizing web enrichment
                      const allLeadership = [
                        ...(Array.isArray(webLeadership) ? webLeadership : typeof webLeadership === 'object' ? Object.values(webLeadership) : []),
                        ...(Array.isArray(linkedInLeadership) ? linkedInLeadership : typeof linkedInLeadership === 'object' ? Object.values(linkedInLeadership) : [])
                      ];
                        
                      // Remove duplicates based on name
                      const uniqueLeadership = allLeadership.filter((leader, index, self) => {
                        const name = typeof leader === 'object' ? leader.name : String(leader);
                        return index === self.findIndex((l) => {
                          const lName = typeof l === 'object' ? l.name : String(l);
                          return lName === name;
                        });
                      });
                      
                      return uniqueLeadership.length > 0 ? (
                          <div className="space-y-4">
                          {uniqueLeadership.map((leader: any, index: number) => (
                              <div key={index} className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                                {typeof leader === 'object' ? (
                                  <>
                                    {leader.name && (
                                      <p className="font-medium text-sm mb-1 text-gray-900">
                                        {leader.name}
                                      </p>
                                    )}
                                    {leader.title && (
                                      <p className="text-xs mb-2 text-gray-600">
                                        {leader.title}
                                      </p>
                                    )}
                                    {leader.linkedin_url && (
                                      <a
                                        href={leader.linkedin_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:underline"
                                      >
                                        View Profile
                                      </a>
                                    )}
                                  </>
                                ) : (
                                  <p className="text-sm text-gray-900">{String(leader)}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-400">
                            <p className="text-sm">No leadership data available</p>
                          </div>
                        );
                    })()}
                  </div>
                )}

                {activeTab === 'job-openings' && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Job Openings</h3>
                    {isLoadingLinkedIn ? (
                      <div className="flex items-center justify-center py-8">
                        <div
                          className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-transparent"
                          style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
                        />
                      </div>
                    ) : linkedInData?.extracted_data ? (
                      (() => {
                        const jobs = linkedInData.extracted_data?.job_openings || 
                                    linkedInData.extracted_data?.jobs || 
                                    linkedInData.extracted_data?.openings || [];
                        const jobsList = Array.isArray(jobs) ? jobs : 
                                        typeof jobs === 'object' ? Object.values(jobs) : [];
                        
                        return jobsList.length > 0 ? (
                          <div className="space-y-4">
                            {jobsList.map((job: any, index: number) => (
                              <div key={index} className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                                {typeof job === 'object' ? (
                                  <>
                                    {job.title && (
                                      <p className="font-medium text-sm mb-1 text-gray-900">
                                        {job.title}
                                      </p>
                                    )}
                                    {job.location && (
                                      <p className="text-xs mb-1 text-gray-600">
                                        📍 {job.location}
                                      </p>
                                    )}
                                    {job.description && (
                                      <p className="text-xs mt-2 text-gray-700">
                                        {job.description}
                                      </p>
                                    )}
                                    {job.url && (
                                      <a
                                        href={job.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:underline mt-2 inline-block"
                                      >
                                        View Job
                                      </a>
                                    )}
                                  </>
                                ) : (
                                  <p className="text-sm text-gray-900">{String(job)}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-400">
                            <p className="text-sm">No job openings available</p>
                          </div>
                        );
                      })()
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <p className="text-sm">No LinkedIn data available</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'similar' && company.id && (
                  <div>
                    <RecommendationsSection
                      companyId={company.id}
                      companyName={company.name}
                      limit={10}
                      onCompanyClick={(companyId) => navigate(`/dashboard/companies/${companyId}`)}
                    />
                  </div>
                )}

                {activeTab === 'web-info' && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Website Enrichment Data</h3>
                    {webEnrichmentData &&
                      isCompanyAdmin &&
                      company?.id &&
                      Object.keys(webEnrichmentData.pending_web_overrides ?? {}).length > 0 && (
                        <WebOverridePendingReview
                          pendingOverrides={webEnrichmentData.pending_web_overrides!}
                          currentValues={webEnrichmentData.extracted_data ?? {}}
                          companyId={company.id}
                          accentColor={COLORS.user.primary}
                          onResolved={(updated) => setWebEnrichmentData(updated)}
                        />
                      )}
                    <WebEnrichmentDisplay
                      webEnrichmentData={webEnrichmentData}
                      isLoading={isLoadingWebEnrichment}
                      accentColor={COLORS.user.primary}
                      canEdit={isCompanyAdmin}
                      companyId={company?.id}
                      onDataUpdated={(updated) => setWebEnrichmentData(updated)}
                    />
                  </div>
                )}

                {activeTab === 'queries' && (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Query History</h3>
                    {queryHistory.length > 0 ? (
                      <div className="space-y-3">
                        {queryHistory.map((query) => (
                          <div
                            key={query.id}
                            className="p-4 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                            style={{ borderColor: '#E5E7EB' }}
                            onClick={() => navigate(`/dashboard/chat/${query.query_id || query.id}`)}
                          >
                            <p className="text-sm font-medium text-gray-900 mb-1">{query.query_text}</p>
                            <p className="text-xs text-gray-500">
                              {query.created_at ? new Date(query.created_at).toLocaleString() : '—'}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No queries yet for this company.</p>
                    )}
                  </div>
                )}
                  </div>
                </div>

            {/* Right Column - Query Section */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg border p-6" style={{ borderColor: '#E5E7EB' }}>
                {/* Blue Gradient Bubble */}
                <div className="mb-4 flex justify-center">
                  <div className="relative w-24 h-24">
                    <div 
                      className="absolute inset-0 rounded-full blur-2xl opacity-60"
                      style={{ 
                        background: `radial-gradient(circle, ${COLORS.user.primary}40 0%, ${COLORS.user.primaryLight}50 50%, transparent 70%)`
                      }}
                    />
                    <div 
                      className="absolute inset-3 rounded-full"
                      style={{ 
                        background: `radial-gradient(circle at 30% 30%, ${COLORS.user.primaryLight} 0%, ${COLORS.user.primary}CC 50%, ${COLORS.user.primary}AA 100%)`,
                        boxShadow: `0 8px 32px ${COLORS.user.primary}4D, inset 0 -8px 16px rgba(255, 255, 255, 0.3), inset 0 8px 16px ${COLORS.user.primary}33`
                      }}
                    />
                  </div>
                </div>
                
                {/* Centered Heading */}
                <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">Ask about {company.name}</h3>
                
                {/* Query Input */}
                <form onSubmit={handleSubmitQuery} className="mb-6">
                  <div className="relative">
                    <input
                      type="text"
                      value={queryText}
                      onChange={(e) => setQueryText(e.target.value)}
                      placeholder="Ask me anything..."
                      disabled={isSubmittingQuery}
                      className="w-full px-4 pr-12 py-3 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: '#FAFAFA' }}
                    />
                    <button
                      type="submit"
                      disabled={isSubmittingQuery || !queryText.trim()}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ 
                        backgroundColor: isSubmittingQuery || !queryText.trim() ? '#E5E7EB' : COLORS.user.primary,
                        color: '#FFFFFF'
                      }}
                    >
                      {isSubmittingQuery ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                      )}
                    </button>
                  </div>
                </form>
              </div>

              {/* LinkedIn Card */}
              {linkedInData && linkedInData.extracted_data && (
                <div className="bg-white rounded-lg border p-6 mt-6" style={{ borderColor: '#E5E7EB' }}>
                  <div className="flex items-center gap-2 mb-4">
                    <svg className="w-5 h-5" style={{ color: '#0077B5' }} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    <h3 className="text-lg font-bold text-gray-900">LinkedIn</h3>
                  </div>

                  {/* Helper function to format numbers */}
                  {(() => {
                    const formatNumber = (num: number): string => {
                      if (num >= 1000000) {
                        return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
                      }
                      if (num >= 1000) {
                        return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
                      }
                      return num.toString();
                    };

                    const extractedData = linkedInData.extracted_data;
                    const followerCount = extractedData?.follower_count;
                    const jobOpenings = extractedData?.job_openings || [];
                    const leadership = extractedData?.leadership || [];
                    const fundingInfo = extractedData?.funding_info;

                    return (
                      <div className="space-y-4">
                        {/* Stats Row */}
                        <div className="grid grid-cols-2 gap-4">
                          {followerCount !== undefined && followerCount !== null && (
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Followers</p>
                              <p className="text-sm font-semibold text-gray-900">
                                {formatNumber(followerCount)}
                              </p>
                            </div>
                          )}
                          {jobOpenings.length > 0 && (
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Open Jobs</p>
                              <p className="text-sm font-semibold text-gray-900">
                                {jobOpenings.length}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Leadership */}
                        {leadership.length > 0 && (
                          <div>
                            <p className="text-xs text-gray-500 mb-2">Key Leadership</p>
                            <div className="flex flex-wrap gap-2">
                              {leadership.slice(0, 5).map((leader: any, index: number) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700"
                                >
                                  {leader.name || leader.title || (typeof leader === 'string' ? leader : JSON.stringify(leader))}
                                </span>
                              ))}
                              {leadership.length > 5 && (
                                <span className="px-2 py-1 rounded text-xs font-medium text-gray-500">
                                  +{leadership.length - 5} more
                                </span>
                                  )}
                            </div>
                          </div>
                        )}

                        {/* Funding Info */}
                        {fundingInfo && (
                          <div>
                            <p className="text-xs text-gray-500 mb-2">Funding</p>
                            <div className="space-y-1">
                              {fundingInfo.last_round_raised && (
                                <p className="text-sm font-semibold text-gray-900">
                                  {fundingInfo.last_round_raised}
                                    </p>
                                  )}
                              {fundingInfo.last_round_type && (
                                <p className="text-xs text-gray-600">
                                  {fundingInfo.last_round_type}
                                  {fundingInfo.last_round_date && (
                                    <span className="ml-2">
                                      ({new Date(fundingInfo.last_round_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })})
                                    </span>
                                  )}
                                </p>
                              )}
                              {fundingInfo.rounds && (
                                <p className="text-xs text-gray-600">
                                  {fundingInfo.rounds} funding round{fundingInfo.rounds !== 1 ? 's' : ''}
                                </p>
                              )}
                            </div>
                        </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Specialties */}
                  {(() => {
                    const specialties = linkedInData.extracted_data?.specialties || 
                                       linkedInData.extracted_data?.specialities || 
                                       linkedInData.extracted_data?.specializations || [];
                    const specialtiesList = Array.isArray(specialties) ? specialties : 
                                           typeof specialties === 'string' ? specialties.split(',').map(s => s.trim()) : 
                                           typeof specialties === 'object' ? Object.values(specialties) : [];
                    
                    return specialtiesList.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold mb-3 text-gray-900">
                          Specialties
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {specialtiesList.map((specialty: any, index: number) => (
                            <span
                              key={index}
                              className="px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
                            >
                              {typeof specialty === 'object' ? specialty.name || JSON.stringify(specialty) : String(specialty)}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </UserSidebarLayout>
  );
};
